import { Box } from '@mantine/core'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getApiError } from '@/api/helpers/apiError'
import {
  useCreateCameras,
  usePollCameraCreationStatus
} from '@/queries/cameraQueries'
import { showToast } from '@/theme/notifications'
import { CameraSource } from '@/types/camera'
import { Device } from '@/types/device'
import { generateUUID } from '@/utils/uuid'
import { LoadingOverlay } from '../ui-shared/LoadingOverlay/LoadingOverlay'
import { AddCameraForm, FormValues } from './AddCameraForm/AddCameraForm'

type TaskState = {
  deviceId: string | null
  pollTaskId: string | null
}

type AddCameraHandlerProps = {
  devices: Device[]
  onCancel: () => void
  onCameraCreated: () => void
}

export const AddCameraHandler = ({
  devices,
  onCancel,
  onCameraCreated
}: AddCameraHandlerProps) => {
  const intl = useIntl()

  const [taskState, setTaskState] = useState<TaskState>({
    deviceId: null,
    pollTaskId: null
  })

  const { deviceId, pollTaskId } = taskState

  const { mutateAsync: createCameras, isPending } = useCreateCameras()

  const {
    data: cameraStatusData,
    isError: isStatusError,
    isLoading: isLoadingStatus
  } = usePollCameraCreationStatus({
    deviceId: deviceId || '',
    pollTaskId: pollTaskId || '',
    enabled: !!deviceId && !!pollTaskId
  })

  useEffect(() => {
    if (cameraStatusData?.status === 'Complete') {
      setTaskState({ deviceId: null, pollTaskId: null })
      showToast(intl.formatMessage({ id: 'cameras.add.success' }), 'success')
      onCameraCreated()
    }

    if (isStatusError || cameraStatusData?.status === 'Failed') {
      setTaskState({ deviceId: null, pollTaskId: null })
      showToast(intl.formatMessage({ id: 'cameras.add.error' }), 'error')
    }
  }, [isStatusError, cameraStatusData?.status, intl, onCameraCreated])

  const isLoading =
    isPending || isLoadingStatus || cameraStatusData?.status === 'Pending'

  const handleCameraCreate = async (values: FormValues) => {
    try {
      await createCameras({
        deviceId: values.deviceId,
        cameras: [
          {
            camera_name: values.cameraName,
            rtsp_url: values.rtspUrl,
            source: CameraSource.Visionplatform
          }
        ]
      })

      setTaskState({
        deviceId: values.deviceId,
        pollTaskId: generateUUID()
      })
    } catch (err) {
      const { errorMessage } = getApiError(err)

      const message =
        errorMessage || intl.formatMessage({ id: 'cameras.add.error' })

      showToast(message, 'error')
    }
  }

  return (
    <Box pos="relative">
      <LoadingOverlay
        visible={isLoading}
        message={<FormattedMessage id="cameras.add.pending" />}
      />

      <AddCameraForm
        devices={devices}
        onCancel={onCancel}
        onSubmit={(values) => void handleCameraCreate(values)}
      />
    </Box>
  )
}
